import * as React from 'react';

type Requirements = {
  equipmentRequired: string;
  participantNumber: string;
  suitability: string;
  areaRequired: string;
  focus: string;
  skillsInvolved: string;
  setUp: string;
};

type ExerciseRequirements =
  "fieldersWarmUp" |
  "generalFieldingPractice" |
  "slipsFieldingExercises" |
  "wicketKeepingExercises" |
  "battingExercises" |
  "bowlingExercises";

interface FormattedRequirementProps {
  exercise: ExerciseRequirements;
}

const requirements = {
  fieldersWarmUp: {
    equipmentRequired: 'TRS; cricket ball.',
    participantNumber: 'Two.',
    suitability: 'All standards.',
    areaRequired: 'Three metres wide to allow for the screen and five metres either side of the screen.',
    focus: 'To progress through simple fielding functions in preparation for more complex drills.',
    skillsInvolved: 'Focus, balance, anticipation, reflexes, catching, stopping the ball.',
    setUp: 'Players stand facing each other either side of the TRS. Each player stands approximately four metres from the TRS.'
  },
  generalFieldingPractice: {
    equipmentRequired: 'TRS; cricket balls; either bat or bowling machine.',
    participantNumber: 'Two at any one time but additional fielders may be used in rotation.',
    suitability: 'ALL standards for Drills 1 & 2. Drill 3 suitable for more advanced players.',
    areaRequired: 'Five metres wide to allow for the screen and clearance and ten metres either side of the screen for the players to vary their distance from the screen.',
    focus: 'To improve out fielding skills and responses.',
    skillsInvolved: 'Focus, balance, anticipation, reflexes, catching, stopping the ball.',
    setUp: 'Players stand facing each other either side of the TRS. Player A has the bat and ball (or controls the bowling machine) and stands about five metres from the screen. Player B stands approximately seven to eight metres from the TRS.'
  },
  slipsFieldingExercises: {
    equipmentRequired: 'Two TRS; cricket balls; bowling machine; ramp (Katchet Training Aid) and Deflection Board.',
    participantNumber: 'One to operate the bowling machine and up to six (up to three either side of the Deflection Board).',
    suitability: 'ALL standards for Drills 1 & 2. Drill 3 suitable for more advanced players.',
    areaRequired: 'Fifteen metres wide to allow for the screen and clearance and ten metres either side of the screen for the players to vary their distance from the screen.',
    focus: 'To improve slips fielding skills and responses.',
    skillsInvolved: 'Focus, balance, anticipation, reflexes, catching, stopping the ball.',
    setUp: 'The set up varies for the different drills.'
  },
  wicketKeepingExercises: {
    equipmentRequired: 'TRS; cricket balls; bowling machine; Deflection Board; set of stumps.',
    participantNumber: 'Wicket Keeper and bowling machine operator. Batsmen may also be incorporated in some drills.',
    suitability: 'Not suitable for beginner and novice keepers. Steps 1 & 2 suitable for competent keepers. Drill 3 suitable for more advanced keepers and batsmen.',
    areaRequired: 'Full practice net pitch.',
    focus: 'To improve wicket keeping skills and responses.',
    skillsInvolved: 'Focus, balance, anticipation, reflexes, catching, stopping the ball.',
    setUp: 'Varies for different drills.'
  },
  battingExercises: {
    equipmentRequired: 'TRS; cricket balls; bowling machine; set of stumps.',
    participantNumber: 'Batsman and bowling machine operator. A wicket keeper may also be incorporated in the drills. A bowler may also be incorporated in the drills in place of the bowling machine.',
    suitability: 'Suitable for competent and more advanced players. Not suitable for beginner and novice standards.',
    areaRequired: 'Full practice net pitch.',
    focus: 'To improve batting skills and responses.',
    skillsInvolved: 'Focus, concentration, balance, anticipation of ball trajectory, stroke playing and selection, reflexes.',
    setUp: 'The batsman takes his place in front of the stumps, the TRS is set up approximately halfway down and across the pitch, the bowling machine is set up in normal delivery position.'
  },
  bowlingExercises: {
    equipmentRequired: 'TRS; cricket balls; set of stumps.',
    participantNumber: 'Bowler. A batsman and/or wicket keeper may also be incorporated in the drills.',
    suitability: 'Suitable for all standards for steps 1 to 5.',
    areaRequired: 'Full practice net pitch.',
    focus: 'To improve bowling skills.',
    skillsInvolved: 'Focus, concentration, balance, line and length, anticipation of ball trajectory.',
    setUp: 'The stumps are put in place, the TRS is set up approximately halfway down and across the pitch. Markings are put on the screens to indicate the stumps position. Different coloured pieces of electrical tape signifying target areas are attached to screens facing the bowler.'
  }
};

const FormattedRequirement = ({ exercise }: FormattedRequirementProps) => {
  let req = requirements[exercise];

  return (
    <p>
      <strong>Equipment required:</strong> {req.equipmentRequired}<br />
      <strong>Number of Participants:</strong> {req.participantNumber}<br />
      <strong>Standard suitability:</strong> {req.suitability}<br />
      <strong>Area Required:</strong> {req.areaRequired}<br />
      <strong>Focus:</strong> {req.focus}<br />
      <strong>Skills Involved:</strong> {req.skillsInvolved}<br />
      <strong>Set Up:</strong> {req.setUp}
    </p>
  )
};

const FieldingProgressions = () => (
  <p>
    (A)	 All of the above drills may be progressed by B returning the ball by throwing or under arming the ball back to a person or target situated at either end of the screen;
    (B)	Drills 1 & 2 may be progressed by B initially standing deeper and advancing towards the screen to catch or pick up the ball at speed. This drill can the be progressed further by returning the ball to a target as set out in Progression (A);
    (C)	Progression B can be varied by using a bowling machine and interposing a narrow-angled deflection board before the TRS. The ball is projected into the Deflection Board which sends the ball at a variety of angles through the screen for fielding. This drill would see two fielders involved with one covering left side and the other covering right side deflections.
  </p>
);

export {
  FormattedRequirement,
  FieldingProgressions
};
